import { IMat4, Mat4 } from "@base/math/Mat4";
import { Vec3 } from "@base/math/Vec3";
import { CacheUtil } from "@base/util/Cache";
import { IDistInOut } from "@biz/definitions/SunSailDefinitions";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";

interface IPoleSegment {
  angle: number;
  length: number;
}

export interface IPoleSegmentExtended extends IPoleSegment {
  matrix: IMat4;
}

export interface IPoleSegmentsExtended {
  segments: IPoleSegmentExtended[];
  topMatrix: IMat4
}
export interface IPoleLengthPosOptions {
  basePos: number;
  up: boolean;
}

export class PoleHelper {
  /**
   *
   * @param length the total length of the pole
   * @param lengthB the length of the pole to the bending point
   * @returns
   */
  static calcLengthA(length: number, lengthB?: number): number {
    return length - (lengthB || 0);
  }

  static getArrayVal(val: number[] | number, index?: number): number {
    if (Array.isArray(val)) {
      return val[index ? index : 0]
    }
    return val;
  }

  private static _calcPoleSegments(length: number, angle: number, lengthB: number): IPoleSegment[] {
    const segments: IPoleSegment[] = [];
    if (PoleLogic.isBent(angle, lengthB)) {
      segments.push({
        angle: 0,
        length: lengthB,
      });
      segments.push({
        angle, // : (10.0 * Math.PI) / 180,
        length: PoleHelper.calcLengthA(length, lengthB),
      });
      // segments.push({
      //   angle,
      //   length: 0.5
      // });
    } else {
      segments.push({
        angle,
        length,
      });
    }
    return segments;
  }

  public static calcPoleExtensionOffsetMatrix(bottomPoleExtension: number, poleOffsetY: number, angle: number): IMat4 {
    let matrix: IMat4 = Mat4.IDENTITY;
    if (poleOffsetY)
      matrix = Mat4.mul(matrix, Mat4.createTranslation(0, poleOffsetY, 0))
    if (bottomPoleExtension)
      matrix = Mat4.mul(matrix, Mat4.createTranslation(Vec3.applyMatrix4(Vec3.from(0, -bottomPoleExtension, 0), Mat4.createRotationX(angle))))
    return matrix;
  }

  private static _calcPoleSegmentsExtendedFromSegments(inputSegments: IPoleSegment[], bottomPoleExtension: number, poleOffsetY: number): IPoleSegmentsExtended {
    if (inputSegments.length === 0) {
      return {
        segments: [],
        topMatrix: Mat4.IDENTITY
      };
    }

    const ret: IPoleSegmentExtended[] = [];
    let matrix = PoleHelper.calcPoleExtensionOffsetMatrix(bottomPoleExtension, poleOffsetY, inputSegments[0].angle)
    for (const s of inputSegments) {
      const rotation: IMat4 = Mat4.createRotationX(s.angle);
      const translation: IMat4 = Mat4.createTranslation(0, s.length, 0);
      matrix = Mat4.mul(matrix, rotation);
      ret.push({
        ...s,
        matrix,
      });
      matrix = Mat4.mul(matrix, translation);
    }
    return {
      segments: ret,
      topMatrix: matrix,
    };
  }

  static _calcPoleSegmentsExtended(_sunSailId: string, _poleIndex: number, length: number, angle: number, lengthB: number, bottomExtension: number, poleOffsetY: number): IPoleSegmentsExtended {
    // console.log("Calc calcPoleSegmentsExtended", PoleHelper.count++, length, angle, lengthB)
    const segments = PoleHelper._calcPoleSegments(length, angle, lengthB);
    return PoleHelper._calcPoleSegmentsExtendedFromSegments(segments, bottomExtension, poleOffsetY);
  }

  static calcPoleSegmentsExtended = CacheUtil.memo(PoleHelper._calcPoleSegmentsExtended, "calcPoleSegmentsExtended", {
    idsFromArgs: 2,
    inputsEqualFn: "shallow"
  })

  static calcMatrixForLengthPosFromSegments(segments: IPoleSegmentsExtended, lengthPos: number, options?: IPoleLengthPosOptions): IMat4 {
    if (options) {
      lengthPos = options.basePos + lengthPos * (options.up ? 1 : -1);
    }

    for (const s of segments.segments) {
      if (s.length >= lengthPos) {
        return Mat4.mul(s.matrix, Mat4.createTranslation(0, lengthPos, 0));
      } else {
        lengthPos -= s.length;
      }
    }
    return Mat4.mul(segments.topMatrix, Mat4.createTranslation(0, lengthPos, 0));
  }

  static getTopOptions(totalLength: number): IPoleLengthPosOptions {
    return {
      basePos: totalLength,
      up: false
    }
  }

  static calcMatrixForLengthPos(sunSailId: string, poleIndex: number, length: number, angle: number, lengthB: number, bottomExtension: number, poleOffsetY: number, lengthPos: number, options?: IPoleLengthPosOptions): IMat4 {

    const segments = PoleHelper.calcPoleSegmentsExtended(sunSailId, poleIndex, length, angle, lengthB, bottomExtension, poleOffsetY);
    return PoleHelper.calcMatrixForLengthPosFromSegments(segments, lengthPos, options);
  }

  static getDistInValue(dist: IDistInOut, index: number): number {
    if (dist.type !== "in")
      return 0;
    if (dist.val === "auto")
      return 0;
    if (Array.isArray(dist.val))
      return dist.val[index < dist.val.length ? index : dist.val.length - 1];
    return dist.val;
  }
}
